import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import socket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import IgnoreModalContext from "../../context/IgnoreModal/IgnoreModalContext";
import {
	canNotifyTicket,
	getUserQueueIds,
	isManagerProfile,
} from "../../helpers/notificationTickets";
import { useDiagnosticRender } from "../../services/frontendDiagnostics";

const REFRESH_INTERVAL_MS = 60000;
const NO_QUEUE_FILTER = [];
const UNREAD_TAB = "unread";
const PENDING_TAB = "pending";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	notificationTabs: {
		minHeight: 44,
		borderBottom: "1px solid " + theme.palette.divider,
	},
	notificationTab: {
		flex: 1,
		minWidth: 0,
		maxWidth: "none",
		minHeight: 44,
		padding: theme.spacing(1),
		textTransform: "none",
	},
	tabLabel: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(0.75),
		whiteSpace: "nowrap",
	},
	tabCount: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: 20,
		height: 20,
		padding: "0 6px",
		borderRadius: 10,
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.secondary.contrastText,
		fontSize: "0.75rem",
		fontWeight: 600,
	},
}));

const NotificationsPopOver = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { handleOpenIgnoreModal } = useContext(IgnoreModalContext);

	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(UNREAD_TAB);
	const [refreshKey, setRefreshKey] = useState(0);
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();
	const lastSoundTimeRef = useRef(0);
	const historyRef = useRef(history);

	const isManager = isManagerProfile(user?.profile);
	const notificationsEnabled = Boolean(user?.id);
	const pendingEnabled =
		notificationsEnabled && user?.profile !== "doctor";
	const userQueueIds = useMemo(
		() => getUserQueueIds(user),
		[user]
	);
	const pendingQueueIds = useMemo(
		() => (isManager ? [] : userQueueIds),
		[isManager, userQueueIds]
	);

	const {
		tickets: pendingTickets,
		count: pendingCount,
		loading: pendingLoading,
	} = useTickets({
		status: "pending",
		showAll: isManager ? "true" : "false",
		queueIds: pendingQueueIds,
		isIgnored: "false",
		refreshKey,
		enabled: pendingEnabled,
		autoCloseStaleTickets: false,
	});

	const {
		tickets: unreadTickets,
		count: unreadCount,
		loading: unreadLoading,
	} = useTickets({
		status: "open",
		showAll: "false",
		queueIds: NO_QUEUE_FILTER,
		withUnreadMessages: "true",
		isIgnored: "false",
		refreshKey,
		enabled: notificationsEnabled,
		autoCloseStaleTickets: false,
	});

	const refreshNotifications = useCallback(() => {
		setRefreshKey(current => current + 1);
	}, []);

	const handleNotifications = useCallback(data => {
		const { message, ticket } = data;
		const contact = data.contact || ticket?.contact || {};
		const messageBody = message?.body || ticket?.lastMessage || "";
		const options = {
			body: `${messageBody} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: String(ticket.id),
			renotify: false,
		};

		if (
			"Notification" in window &&
			Notification.permission === "granted"
		) {
			const notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact.name || ""}`,
				options
			);

			notification.onclick = event => {
				event.preventDefault();
				window.focus();
				historyRef.current.push(`/tickets/${ticket.id}`);
				notification.close();
			};
		}

		const now = Date.now();
		if (now - lastSoundTimeRef.current > 3000) {
			soundAlertRef.current?.();
			lastSoundTimeRef.current = now;
		}
	}, []);

	useDiagnosticRender("NotificationsPopOver", () => ({
		counts: {
			pending: pendingCount,
			unread: unreadCount,
			open: isOpen ? 1 : 0,
			loading: pendingLoading || unreadLoading ? 1 : 0,
		},
	}));

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser does not support notifications");
		} else if (Notification.permission === "default") {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		const handleConnect = () => {
			socket.emit("joinNotification");
			refreshNotifications();
		};

		const handleTicket = () => {
			refreshNotifications();
		};

		const handleAppMessage = data => {
			refreshNotifications();

			if (
				data.action === "create" &&
				!data.message?.read &&
				canNotifyTicket(data.ticket, user)
			) {
				handleNotifications(data);
			}
		};

		socket.on("connect", handleConnect);
		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);

		if (socket.connected) {
			handleConnect();
		}

		return () => {
			socket.off("connect", handleConnect);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
		};
	}, [handleNotifications, refreshNotifications, user]);

	useEffect(() => {
		const handleFocus = () => refreshNotifications();
		const intervalId = window.setInterval(
			refreshNotifications,
			REFRESH_INTERVAL_MS
		);

		window.addEventListener("focus", handleFocus);

		return () => {
			window.removeEventListener("focus", handleFocus);
			window.clearInterval(intervalId);
		};
	}, [refreshNotifications]);

	const handleClick = () => {
		if (!isOpen) {
			setActiveTab(UNREAD_TAB);
		}

		setIsOpen(current => !current);
	};
	const handleClickAway = () => setIsOpen(false);
	const handleTicketSelect = ticket => {
		setIsOpen(false);
		history.push(`/tickets/${ticket.id}`);
	};

	const renderTickets = (tickets, { hideMessagePreview = false } = {}) =>
		tickets.map(ticket => (
			<div onClick={handleClickAway} key={ticket.id}>
				<TicketListItem
					ticket={ticket}
					onTicketSelect={() => handleTicketSelect(ticket)}
					onOpenIgnoreModal={handleOpenIgnoreModal}
					hideFavorite
					hideMessagePreview={hideMessagePreview}
				/>
			</div>
		));

	const totalNotificationCount = pendingCount + unreadCount;
	const activeTickets =
		activeTab === UNREAD_TAB ? unreadTickets : pendingTickets;
	const hideActiveMessagePreview = activeTab === PENDING_TAB;

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={totalNotificationCount} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>

			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<Tabs
					value={activeTab}
					onChange={(_, nextTab) => setActiveTab(nextTab)}
					variant="fullWidth"
					indicatorColor="primary"
					textColor="primary"
					className={classes.notificationTabs}
					aria-label="Categorías de notificaciones"
				>
					<Tab
						value={UNREAD_TAB}
						className={classes.notificationTab}
						label={
							<span className={classes.tabLabel}>
								{i18n.t("notifications.unreadTickets")}
								<span className={classes.tabCount}>{unreadCount}</span>
							</span>
						}
					/>
					<Tab
						value={PENDING_TAB}
						className={classes.notificationTab}
						label={
							<span className={classes.tabLabel}>
								{i18n.t("notifications.pendingTickets")}
								<span className={classes.tabCount}>{pendingCount}</span>
							</span>
						}
					/>
				</Tabs>

				<List dense className={classes.tabContainer}>
					{activeTickets.length > 0
						? renderTickets(activeTickets, {
								hideMessagePreview: hideActiveMessagePreview,
						  })
						: (
							<ListItem>
								<ListItemText>
									{i18n.t("notifications.noTickets")}
								</ListItemText>
							</ListItem>
						)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
